import React from "react";
import "./style.css";
import { Card } from "react-bootstrap";
import { activeMortalityAssessmentSelector } from "../../../../reducers";
import { connect } from "react-redux";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Chart, Line, Bar } from "react-chartjs-2";

import { ResponsiveContainer } from "recharts";
import { createGradientColors, createHighestValueColors } from "./utils";

const MortalityAssessmentCurveGraphs = ({ mortalityAssessmentInfo, ...props }) => {
  const curvesData = mortalityAssessmentInfo?.curve_data;
  const stdCurvesData = mortalityAssessmentInfo?.std_curve_data;
  const initialAge = mortalityAssessmentInfo?.curve_assessment_age;
  if (!curvesData || !initialAge) return null;
  if (!stdCurvesData || !initialAge) return null;
  const durationsMap = [];
  curvesData.forEach(row => {
    if (durationsMap === []) {
      durationsMap.push({ x: row.duration, y: 0 });
    } else if (durationsMap.some(element => element.x === row.duration)) {
    } else {
      durationsMap.push({ x: row.duration, y: 0, age: Number(initialAge) + row.duration });
    }
  });

  let qxCurvesData = durationsMap.map(row => {
    const accumulated_value = curvesData.reduce((accumulator, currentElem) => {
      if (currentElem.duration === row.x) {
        return currentElem.qx + accumulator;
      } else {
        return accumulator;
      }
    }, 0);
    return { x: row.x, y: accumulated_value / 12, age: row.age };
  });
  qxCurvesData.unshift({ x: 0, y: 0, age: Number(initialAge) });

  let stdQxCurvesData = durationsMap.map(row => {
    const accumulated_value = stdCurvesData.reduce((accumulator, currentElem) => {
      if (currentElem.duration === row.x) {
        return currentElem.qx + accumulator;
      } else {
        return accumulator;
      }
    }, 0);
    return { x: row.x, y: accumulated_value / 12, age: row.age };
  });
  stdQxCurvesData.unshift({ x: 0, y: 0, age: Number(initialAge) });

  let survivalCurvesData = durationsMap.map(row => {
    const accumulated_value = curvesData.reduce((accumulator, currentElem) => {
      if (currentElem.duration === row.x) {
        return currentElem.eom_lives + accumulator;
      } else {
        return accumulator;
      }
    }, 0);
    return { x: row.x, y: accumulated_value / 12, age: row.age };
  });
  survivalCurvesData.unshift({ x: 0, y: 1, age: Number(initialAge) });

  let stdSurvivalCurvesData = durationsMap.map(row => {
    const accumulated_value = stdCurvesData.reduce((accumulator, currentElem) => {
      if (currentElem.duration === row.x) {
        return currentElem.eom_lives + accumulator;
      } else {
        return accumulator;
      }
    }, 0);
    return { x: row.x, y: accumulated_value / 12, age: row.age };
  });
  stdSurvivalCurvesData.unshift({ x: 0, y: 1, age: Number(initialAge) });

  let deathCurveData = durationsMap.map(row => {
    const accumulated_value = curvesData.reduce((accumulator, currentElem) => {
      if (currentElem.duration === row.x) {
        return currentElem.monthly_deaths + accumulator;
      } else {
        return accumulator;
      }
    }, 0);
    return { x: row.x, y: accumulated_value, age: row.age };
  });
  deathCurveData.unshift({ x: 0, y: 0, age: Number(initialAge) });

  let stdDeathCurveData = durationsMap.map(row => {
    const accumulated_value = stdCurvesData.reduce((accumulator, currentElem) => {
      if (currentElem.duration === row.x) {
        return currentElem.monthly_deaths + accumulator;
      } else {
        return accumulator;
      }
    }, 0);
    return { x: row.x, y: accumulated_value, age: row.age };
  });
  stdDeathCurveData.unshift({ x: 0, y: 0, age: Number(initialAge) });

  let probPassingData = durationsMap.map(row => {
    const accumulated_value = curvesData.reduce((accumulator, currentElem) => {
      if (currentElem.duration === row.x) {
        return currentElem.eom_lives;
      } else {
        return accumulator;
      }
    }, 0);
    return { x: row.x, y: 1 - accumulated_value, age: row.age };
  });

  const cutOffPoint = survivalCurvesData.findIndex(elem => elem.y < 0.001);
  if (cutOffPoint > 1) {
    qxCurvesData = qxCurvesData.slice(0, cutOffPoint);
    survivalCurvesData = survivalCurvesData.slice(0, cutOffPoint);
    deathCurveData = deathCurveData.slice(0, cutOffPoint);
  }
  const stdCutOffPoint = stdSurvivalCurvesData.findIndex(elem => elem.y < 0.001);
  if (cutOffPoint > 1) {
    stdQxCurvesData = stdQxCurvesData.slice(0, cutOffPoint);
    stdSurvivalCurvesData = stdSurvivalCurvesData.slice(0, cutOffPoint);
    stdDeathCurveData = stdDeathCurveData.slice(0, cutOffPoint);
  }

  const getChartOptions = title => {
    return {
      responsive: true,
      plugins: {
        legend: {
          position: "top",
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              try {
                const parsed_label = context.label.split(",");
                return `year: ${parsed_label[0]} - age: ${parsed_label[1]} = ${context.formattedValue}`;
              } catch {
                return context.dataset.label || "";
              }
            },
          },
        },
        title: {
          display: true,
          text: title,
        },
        scales: {
          xAxis: {
            // The axis for this scale is determined from the first letter of the id as `'x'`
            // It is recommended to specify `position` and / or `axis` explicitly.
            type: "number",
          },
        },
      },
    };
  };

  ChartJS.register(CategoryScale, LinearScale, PointElement, BarElement, LineElement, Title, Tooltip, Legend);
  const currentYear = new Date().getFullYear();
  const curvesLabels = qxCurvesData.map((elem, index) => [elem.x, elem.age]);
  const probOFPassingLabels = qxCurvesData.map((elem, index) => [elem.x, elem.age, currentYear + index]);
  const qxCurvesDataY = qxCurvesData.map(elem => elem.y);
  const stdQxCurvesDataY = stdQxCurvesData.map(elem => elem.y);
  const survivalCurvesDataY = survivalCurvesData.map(elem => elem.y);
  const deathCurveDataY = deathCurveData.map(elem => elem.y);
  const stdDeathCurveDataY = stdDeathCurveData.map(elem => elem.y);
  const probOfPassingDataY = probPassingData.map(elem => elem.y);
  const median50Data = probPassingData.map(() => 0.5).slice(0, probPassingData.findIndex(elem => elem.y > 0.5) + 1);
  const median85Data = probPassingData.map(() => 0.85).slice(0, probPassingData.findIndex(elem => elem.y > 0.85) + 1);
  const median95Data = probPassingData.map(() => 0.95).slice(0, probPassingData.findIndex(elem => elem.y > 0.95) + 1);

  const getCurvesDataObj = (labels, data, title) => {
    return {
      labels: labels,
      datasets: [
        {
          label: title || "",
          data: data,
          borderColor: "#00b050",
          backgroundColor: "#00b050",
          cubicInterpolationMode: "monotone",
          fill: false,
        },
      ],
    };
  };

  const getQxCurvesDataObj = (labels, data, title) => {
    return {
      labels: labels,
      datasets: [
        {
          label: title || "",
          data: data,
          borderColor: createGradientColors(data, 1),
          backgroundColor: createGradientColors(data, 1),
          cubicInterpolationMode: "monotone",
          fill: false,
        },
      ],
    };
  };

  const getDeathCurvesDataObj = (labels, data, title) => {
    return {
      labels: labels,
      datasets: [
        {
          label: title || "",
          data: data,
          borderColor: createHighestValueColors(data, 1),
          backgroundColor: createHighestValueColors(data, 1),
          cubicInterpolationMode: "monotone",
          fill: false,
        },
      ],
    };
  };

  const getComposedCurvesDataObj = (labels, data, mortalityAssessmentInfo, mediansArrays, title) => {
    const median50 = mortalityAssessmentInfo?.curve_median_months
      ? (mortalityAssessmentInfo?.curve_median_months / 12).toFixed(2)
      : 0;
    const median85 = mortalityAssessmentInfo?.curve_median_85_months
      ? (mortalityAssessmentInfo?.curve_median_85_months / 12).toFixed(2)
      : 0;
    const median95 = mortalityAssessmentInfo?.curve_median_95_months
      ? (mortalityAssessmentInfo?.curve_median_95_months / 12).toFixed(2)
      : 0;
    return {
      labels: labels,
      datasets: [
        {
          type: "line",
          label: `median 50: ${median50}`,
          data: mediansArrays?.median50Data || [],
          borderColor: "purple",
          backgroundColor: "purple",
          cubicInterpolationMode: "monotone",
          fill: false,
        },
        {
          type: "line",
          label: `median 85: ${median85}`,
          data: mediansArrays?.median85Data || [],
          borderColor: "#00b050",
          backgroundColor: "#00b050",
          cubicInterpolationMode: "monotone",
          fill: false,
        },
        {
          type: "line",
          label: `median 95: ${median95}`,
          data: mediansArrays?.median95Data || [],
          borderColor: "black",
          backgroundColor: "black",
          cubicInterpolationMode: "monotone",
          fill: false,
        },
        {
          type: "bar",
          label: title || "",
          data: data,
          borderColor: "#36454F",
          backgroundColor: "#36454F",
          cubicInterpolationMode: "monotone",
          fill: false,
        },
      ],
    };
  };

  return (
    <>
      <Card>
        <Card.Body style={{ display: "flex", flexDirection: "column" }}>
          <div style={{ border: "1px solid gray", padding: "0.5rem 0.5rem", display: "flex" }}>
            <ResponsiveContainer width="100%" height={470}>
              <Line options={getChartOptions("Qx Curve")} data={getCurvesDataObj(curvesLabels, qxCurvesDataY, "Qx")} />
            </ResponsiveContainer>
          </div>
          <div style={{ border: "1px solid gray", padding: "0.5rem 0.5rem", display: "flex" }}>
            <ResponsiveContainer width="100%" height={470}>
              <Line
                options={getChartOptions("Survival Curve")}
                data={getCurvesDataObj(curvesLabels, survivalCurvesDataY, "EOM Lives")}
              />
            </ResponsiveContainer>
          </div>
          <div style={{ border: "1px solid gray", padding: "0.5rem 0.5rem", display: "flex" }}>
            <ResponsiveContainer width="100%" height={470}>
              <Line
                options={getChartOptions("Death Curve")}
                data={getCurvesDataObj(curvesLabels, deathCurveDataY, "Monthly Deaths")}
              />
            </ResponsiveContainer>
          </div>
        </Card.Body>
        <Card.Body>
          <div style={{ border: "1px solid gray", padding: "0.5rem 0.5rem", display: "flex" }}>
            <ResponsiveContainer height={470}>
              <Chart
                options={getChartOptions("Prob of Passing Curve")}
                data={getComposedCurvesDataObj(
                  probOFPassingLabels,
                  probOfPassingDataY,
                  mortalityAssessmentInfo,
                  { median50Data, median85Data, median95Data },
                  "Monthly Deaths",
                )}
              />
            </ResponsiveContainer>
          </div>
          <div style={{ width: "100%", display: "grid", gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr" }}>
            <ResponsiveContainer height={470} style={{ border: "0.2px solid gray", borderTop: "none" }}>
              <Bar
                options={getChartOptions("Death Curve")}
                data={getDeathCurvesDataObj(curvesLabels, deathCurveDataY, "Monthly Deaths")}
              />
            </ResponsiveContainer>
            <ResponsiveContainer
              height={470}
              style={{ border: "0.2px solid gray", borderLeft: "none", borderTop: "none" }}
            >
              <Bar options={getChartOptions("Qx Curve")} data={getQxCurvesDataObj(curvesLabels, qxCurvesDataY, "Qx")} />
            </ResponsiveContainer>
            <ResponsiveContainer height={470} style={{ border: "0.2px solid gray", borderTop: "none" }}>
              <Bar
                options={getChartOptions("STD Death Curve")}
                data={getDeathCurvesDataObj(curvesLabels, stdDeathCurveDataY, "Monthly Deaths")}
              />
            </ResponsiveContainer>
            <ResponsiveContainer
              height={470}
              style={{ border: "0.2px solid gray", borderLeft: "none", borderTop: "none" }}
            >
              <Bar
                options={getChartOptions("STD Qx Curve")}
                data={getQxCurvesDataObj(curvesLabels, stdQxCurvesDataY, "Qx")}
              />
            </ResponsiveContainer>
          </div>
        </Card.Body>
      </Card>
    </>
  );
};

const mapStateToProps = state => {
  return {
    mortalityAssessmentInfo: activeMortalityAssessmentSelector(state),
  };
};

export default connect(mapStateToProps, {})(MortalityAssessmentCurveGraphs);
